/** @format */

import {Fragment, ReactNode} from 'react';

import {Box} from '@mui/material';

import {
  MultilineChartRoot as Root,
  MultilineChartClasses as classes,
  ChartsContainer,
  NormalChartsContainer,
} from './styles';
import MainChart from './components/main-chart';
import {
  CHARTS_MARGIN_BOTTOM,
  CHARTS_MARGIN_LEFT,
  CHARTS_MARGIN_RIGHT,
  CHARTS_MARGIN_TOP,
  X_AXIS_HEIGHT,
  Y_AXIS_WIDTH,
} from './const';
import CustomBrush from './components/brush';
import {
  MultilineChartInterface,
  type ChartTypeSettings,
  HeightParams,
  ChartScaleSettings,
} from './model/common';
import getFakeChart from './helpers/getFakeChart';
import {HoverBox} from './components/hover-box';
import XAxisWithGrid from './components/x-axis-with-grid';
import XAxisWithTodayMarker from './components/x-axis-with-today-marker';
import {LabelBox} from './components/label-box';
import {ScatterChart} from './components/scatter-chart';
import {getLogarithmicData} from './helpers/getLogarithmicData';

interface Props<ChartId extends string> {
  data: MultilineChartInterface<ChartId>;
  heightParams: HeightParams;
  syncId: string;
  selectedChartIds: ChartId[];
  onChartClick: (chartId: ChartId) => void;
  chartTypeSettings: ChartTypeSettings<ChartId>;
  chartScaleSettings: ChartScaleSettings<ChartId>;
  children?: ReactNode;
  planLineDashed?: boolean;
  yAxisFromZero?: boolean;
  // Range-scrubber brush below the chart. Default true — DigitalCast and every existing
  // caller keep the brush. Compact overview cards pass false (no room, no need to zoom).
  showBrush?: boolean;
}

const MultilineChart = <ChartId extends string>({
  data,
  syncId,
  selectedChartIds,
  onChartClick,
  chartTypeSettings,
  chartScaleSettings,
  heightParams,
  children,
  planLineDashed,
  yAxisFromZero,
  showBrush = true,
}: Props<ChartId>) => {
  const {charts, tooltipDataList, xAxisDataList} = data;
  const {containerHeight, boxHeight, blockHeight, overallHeight} = heightParams;

  const selectedCharts = charts.filter(chart => selectedChartIds.includes(chart.chartId));

  const selectedChartsWithLogarithms = selectedCharts.map(chart => {
    const chartType = chartTypeSettings[chart.chartId] || 'primary';
    const isLogarithmic = Boolean(chartScaleSettings[chart.chartId]) && chartType === 'primary';
    if (!isLogarithmic) return chart;
    return {
      ...chart,
      logarithmData: getLogarithmicData(chart.chartsData),
    };
  });

  const usualCharts = selectedChartsWithLogarithms.filter(chart => !chart.isScatter);
  const scatterCharts = selectedChartsWithLogarithms.filter(chart => chart.isScatter);

  const fakeChart = getFakeChart(selectedChartsWithLogarithms);

  // Сингловый режим: выбран только один график
  const isSingle = selectedChartsWithLogarithms.length === 1;

  // Мы показываем вертикальную ось только в сингловом режиме
  const yAxisWidth = isSingle ? Y_AXIS_WIDTH : 0;

  const xAxisMargin = {
    right: CHARTS_MARGIN_RIGHT,
    left: CHARTS_MARGIN_LEFT + yAxisWidth,
    top: 6,
  };
  const xAxisHeight = blockHeight - 4;

  return (
    <Root height={`${overallHeight}px`}>
      <Box className={classes.container}>
        <XAxisWithGrid
          chart={fakeChart}
          xAxisDataList={xAxisDataList}
          syncId={syncId}
          height={xAxisHeight}
          margin={xAxisMargin}
        />

        <XAxisWithTodayMarker
          chart={fakeChart}
          xAxisDataList={xAxisDataList}
          syncId={syncId}
          height={xAxisHeight}
          margin={xAxisMargin}
        />

        {showBrush && (
          <CustomBrush chart={fakeChart} syncId={syncId} top={`${blockHeight + 22}px`} />
        )}

        <ChartsContainer height={`${containerHeight}px`} top={`${X_AXIS_HEIGHT}px`}>
          <NormalChartsContainer height={`${boxHeight}px`} data-testid='main-charts-box'>
            {scatterCharts.map(chart => (
              <Fragment key={chart.chartId}>
                <LabelBox chart={chart} />
                <HoverBox color={chart.color} disabled={chart.chartId !== 'notes'}>
                  <ScatterChart
                    data={chart.chartsData}
                    syncId={syncId}
                    margin={{
                      left: CHARTS_MARGIN_LEFT,
                      right: CHARTS_MARGIN_RIGHT,
                      top: CHARTS_MARGIN_TOP,
                      bottom: CHARTS_MARGIN_BOTTOM,
                    }}
                  />
                </HoverBox>
              </Fragment>
            ))}
            {usualCharts.map((chart, index) => (
              <Fragment key={chart.chartId}>
                <LabelBox chart={chart} />
                <HoverBox color={chart.color} disabled={isSingle}>
                  <MainChart
                    syncId={syncId}
                    onChartClick={onChartClick}
                    chart={chart}
                    allCharts={selectedChartsWithLogarithms}
                    chartIndex={index}
                    tooltipData={tooltipDataList}
                    chartTypeSettings={chartTypeSettings}
                    margin={{
                      left: CHARTS_MARGIN_LEFT,
                      right: CHARTS_MARGIN_RIGHT,
                      top: CHARTS_MARGIN_TOP,
                      bottom: CHARTS_MARGIN_BOTTOM,
                    }}
                    canShowYAxis
                    openedScattersCount={scatterCharts.length}
                    planLineDashed={planLineDashed}
                    yAxisFromZero={yAxisFromZero}
                  />
                </HoverBox>
              </Fragment>
            ))}
            {children}
          </NormalChartsContainer>
        </ChartsContainer>
      </Box>
    </Root>
  );
};

export default MultilineChart;
